/**
 * Centralized category system - Single source of truth for all expense categories
 * This file consolidates all category definitions to ensure consistency across the app
 */

export interface CategoryInfo {
  id: string;
  label: string;
  emoji: string;
}

// Primary category map - canonical source for all category data
export const CATEGORY_MAP: Record<string, CategoryInfo> = {
  food: { id: 'food', label: 'Food & Dining', emoji: '🍕' },
  groceries: { id: 'groceries', label: 'Groceries', emoji: '🛒' },
  transport: { id: 'transport', label: 'Transportation', emoji: '🚗' },
  shopping: { id: 'shopping', label: 'Shopping', emoji: '🛍️' },
  entertainment: { id: 'entertainment', label: 'Entertainment', emoji: '🎬' },
  subscriptions: { id: 'subscriptions', label: 'Subscriptions', emoji: '📺' },
  utilities: { id: 'utilities', label: 'Bills & Utilities', emoji: '💡' },
  health: { id: 'health', label: 'Healthcare', emoji: '🏥' },
  education: { id: 'education', label: 'Education', emoji: '📚' },
  travel: { id: 'travel', label: 'Travel', emoji: '✈️' },
  home: { id: 'home', label: 'Home & Garden', emoji: '🏠' },
  loans: { id: 'loans', label: 'Loans', emoji: '🏦' },
  credit_cards: { id: 'credit_cards', label: 'Credit Cards', emoji: '💳' },
  other: { id: 'other', label: 'Other', emoji: '📦' },
} as const;

// List of all categories for iteration
export const CATEGORIES_LIST: CategoryInfo[] = Object.values(CATEGORY_MAP);

// Quick access array for forms and selects
export const CATEGORY_OPTIONS = CATEGORIES_LIST.map(cat => ({
  id: cat.id,
  label: cat.label,
  emoji: cat.emoji,
}));

/**
 * Custom category lookup map (UUID -> {label, emoji}).
 * Populated at runtime by useCustomCategories and admin views.
 */
const customCategoryMap: Record<string, { label: string; emoji: string }> = {};

export function setCustomCategoryMap(entries: Array<{ id: string; label: string; emoji: string }>): void {
  entries.forEach(({ id, label, emoji }) => {
    if (id) customCategoryMap[id] = { label, emoji: emoji || '🏷️' };
  });
}

export function getCustomCategoryMap(): Record<string, { label: string; emoji: string }> {
  return customCategoryMap;
}

/**
 * Get the emoji for a category by ID or label
 * Falls back to 📦 for unknown categories
 */
export function getCategoryEmoji(category: string): string {
  if (!category) return '📦';

  // Custom category UUID lookup (case-sensitive - UUIDs are stored as-is)
  if (customCategoryMap[category]) {
    return customCategoryMap[category].emoji;
  }

  const normalizedCategory = category.toLowerCase();

  // Try direct ID match first
  if (CATEGORY_MAP[normalizedCategory]) {
    return CATEGORY_MAP[normalizedCategory].emoji;
  }
  
  // Try matching by label (case-insensitive)
  const byLabel = CATEGORIES_LIST.find(
    cat => cat.label.toLowerCase() === normalizedCategory
  );
  if (byLabel) {
    return byLabel.emoji;
  }
  
  // Partial match on label
  const partialMatch = CATEGORIES_LIST.find(
    cat => cat.label.toLowerCase().includes(normalizedCategory) ||
           normalizedCategory.includes(cat.id)
  );
  if (partialMatch) {
    return partialMatch.emoji;
  }
  
  return '📦'; // Default fallback
}

/**
 * Get the display label for a category by ID
 * Falls back to the original string with title case for unknown categories
 */
export function getCategoryLabel(category: string, translate?: (key: string) => string): string {
  const labelFor = (info: CategoryInfo) => translate ? translate(`common:categories.${info.id}`) : info.label;
  if (!category) return labelFor(CATEGORY_MAP.other);

  // Custom category UUID lookup
  if (customCategoryMap[category]) {
    return customCategoryMap[category].label;
  }

  const normalizedCategory = category.toLowerCase();

  // Try direct ID match
  if (CATEGORY_MAP[normalizedCategory]) {
    return labelFor(CATEGORY_MAP[normalizedCategory]);
  }
  
  // Try matching by label
  const byLabel = CATEGORIES_LIST.find(
    cat => cat.label.toLowerCase() === normalizedCategory
  );
  if (byLabel) {
    return labelFor(byLabel);
  }
  
  // Return original with title case
  return category.charAt(0).toUpperCase() + category.slice(1);
}

/**
 * Get full category info by ID or label
 * Returns a fallback object for unknown categories
 */
export function getCategoryInfo(category: string): CategoryInfo {
  if (!category) return { id: 'other', label: 'Other', emoji: '📦' };

  // Custom category UUID lookup
  if (customCategoryMap[category]) {
    return {
      id: category,
      label: customCategoryMap[category].label,
      emoji: customCategoryMap[category].emoji,
    };
  }

  const normalizedCategory = category.toLowerCase();

  // Try direct ID match
  if (CATEGORY_MAP[normalizedCategory]) {
    return CATEGORY_MAP[normalizedCategory];
  }
  
  // Try matching by label
  const byLabel = CATEGORIES_LIST.find(
    cat => cat.label.toLowerCase() === normalizedCategory
  );
  if (byLabel) {
    return byLabel;
  }
  
  // Return fallback
  return {
    id: category.toLowerCase(),
    label: category.charAt(0).toUpperCase() + category.slice(1),
    emoji: '📦',
  };
}

/**
 * Keywords for AI category suggestion matching
 * Used by the suggest-category edge function
 */
export const CATEGORY_KEYWORDS: Record<string, string[]> = {
  food: ['restaurant', 'lunch', 'dinner', 'breakfast', 'cafe', 'coffee', 'pizza', 'burger', 'sushi', 'takeout', 'delivery', 'uber eats', 'doordash', 'grubhub'],
  groceries: ['grocery', 'supermarket', 'walmart', 'costco', 'whole foods', 'trader joe', 'safeway', 'kroger', 'aldi', 'market', 'produce'],
  transport: ['gas', 'fuel', 'uber', 'lyft', 'taxi', 'parking', 'toll', 'bus', 'train', 'metro', 'subway', 'car wash', 'oil change', 'mechanic', 'auto'],
  entertainment: ['movie', 'concert', 'game', 'theater', 'museum', 'bar', 'club', 'party', 'gaming', 'arcade', 'bowling', 'karaoke'],
  subscriptions: ['netflix', 'spotify', 'hulu', 'disney', 'hbo', 'amazon prime', 'youtube premium', 'apple music', 'membership', 'subscription', 'monthly', 'streaming', 'gym membership', 'patreon'],
  health: ['pharmacy', 'doctor', 'hospital', 'clinic', 'dental', 'dentist', 'medicine', 'prescription', 'gym', 'fitness', 'vitamin', 'therapy', 'wellness'],
  home: ['rent', 'mortgage', 'furniture', 'ikea', 'home depot', 'lowes', 'repair', 'maintenance', 'cleaning', 'garden', 'decor', 'appliance'],
  shopping: ['amazon', 'target', 'clothing', 'shoes', 'electronics', 'gift', 'mall', 'outlet', 'online', 'fashion', 'accessories', 'jewelry'],
  utilities: ['electric', 'water', 'internet', 'phone', 'mobile', 'cable', 'wifi', 'verizon', 'att', 'comcast', 'utility', 'bill'],
  education: ['tuition', 'books', 'course', 'class', 'school', 'college', 'university', 'training', 'certification'],
  travel: ['flight', 'hotel', 'airbnb', 'vacation', 'trip', 'airline', 'luggage', 'resort'],
  loans: ['loan', 'mortgage payment', 'car payment', 'student loan', 'personal loan', 'installment', 'emi', 'repayment'],
  credit_cards: ['credit card', 'visa', 'mastercard', 'amex', 'card payment', 'minimum payment', 'credit bill', 'card bill'],
};

/**
 * Get recent categories from localStorage, sorted by most recent usage
 */
export function getRecentCategories(): string[] {
  try {
    const stored = localStorage.getItem('recentCategories');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Track a category as recently used in localStorage
 */
export function trackRecentCategory(catId: string): void {
  if (catId === 'other') return;
  try {
    const recent = getRecentCategories().filter(id => id !== catId);
    recent.unshift(catId);
    localStorage.setItem('recentCategories', JSON.stringify(recent.slice(0, 10)));
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Sort a list of category objects by recent usage
 */
export function sortCategoriesByRecent<T extends { id: string }>(categories: T[]): T[] {
  const recentIds = getRecentCategories();
  return [...categories].sort((a, b) => {
    const aIndex = recentIds.indexOf(a.id);
    const bIndex = recentIds.indexOf(b.id);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return 0;
  });
}
